import { combineReducers } from 'redux';
import { call, delay, put, putResolve, select } from 'redux-saga/effects';

import { environment } from 'environments/environment';
import { UrlSetup } from 'environments/Type';
import { ExtendedAxiosResponse } from 'helpers/apiClient';
import { createActionCreator, createReducer } from 'helpers/redux/reduxHelpers';
import { AppState } from 'helpers/store/models/AppState';
import { ApiClientRequest } from 'models/ApiClientRequest';
import { UserInfo } from 'models/UserInfo';
import { api } from 'saga/auth/api';
import { handleGetUserInfo, loginActions, selectUserInfo } from 'saga/auth/ducks';
import { getKahlActions, getKahlOutsideComponentAction } from 'saga/kahl/ducks';
import { selectProperty } from 'saga/property/ducks';
import { userActivityTimeAction } from 'saga/userActivity/ducks';

/* STATE */
export interface ApiClientState {
  /**
   * true: following requests needs to be stopped mostly because of JWT validTo expired or for other specific reasons
   * false: not necessary to block any API call
   */
  allApiCallBarrier: boolean;
}

/* ACTION TYPES */
export enum ApiClientActionTypes {
  setAllApiCallBarrier = '@@API_CLIENT/SET_ALL_API_BARRIER',
}

/* ACTIONS */
export const setAllApiCallBarrierAction = createActionCreator(ApiClientActionTypes.setAllApiCallBarrier);

/* REDUCERS */
export const initialState: ApiClientState = {
  allApiCallBarrier: false,
};

const allApiCallBarrier = createReducer(initialState.allApiCallBarrier, {
  [ApiClientActionTypes.setAllApiCallBarrier]: (_state: boolean, payload: boolean) => payload,
});

export default combineReducers<ApiClientState>({
  allApiCallBarrier,
});

/* SELECTORS */
const selectApiClientState = (state: AppState) => state.apiClient || {};

export const isAllApiCallBarrier = (state: AppState): boolean => {
  const apiClientState: ApiClientState = selectApiClientState(state);

  return apiClientState.allApiCallBarrier;
};

/* SAGAS */
function* setRefreshJwtBarrierEnabled() {
  const refreshJwtBarrierEnabled: boolean = yield select(isAllApiCallBarrier);

  if (!refreshJwtBarrierEnabled) {
    yield putResolve(setAllApiCallBarrierAction(true));

    return true;
  }

  return false;
}

function* doRefreshJwt(clientRequest: ApiClientRequest): any {
  console.log(`JWT expired:: Pause request: ${clientRequest.apiCall.payload.fn.name}`);
  console.log('JWT expired:: Refresh JWT token.');

  const refreshJwtBarrierWasJustSet: boolean = yield call(setRefreshJwtBarrierEnabled);

  if (refreshJwtBarrierWasJustSet) {
    const refreshJWTTokenResp: ExtendedAxiosResponse = yield call(
      api.refreshJWTToken,
      environment.buildProperties.bffAuthUrl
    );

    if (!refreshJWTTokenResp.ok) {
      console.log('Unable to refresh JWT token. Redirecting to CAS Login');
      yield putResolve(loginActions.request());

      return {};
    }

    const getUserInfoResp: ExtendedAxiosResponse = yield call(api.getUserInfo, environment.buildProperties.bffAuthUrl);

    yield call(handleGetUserInfo, getUserInfoResp);

    // perform original request
    console.log(`JWT expired:: Continue request: ${clientRequest.apiCall.payload.fn.name}`);

    const response: ExtendedAxiosResponse = yield clientRequest.apiCall;

    // unblock waiting requests
    yield put(setAllApiCallBarrierAction(false));

    return response;
  } else {
    // wait and then perform original request
    console.log(`JWT being refreshed:: Wait: ${clientRequest.apiCall.payload.fn.name}`);

    let refreshJwtBarrierEnabled: boolean = yield select(isAllApiCallBarrier);

    while (refreshJwtBarrierEnabled) {
      yield delay(10); // protection delay; without it, the loop will automatically break after a while
      refreshJwtBarrierEnabled = yield select(isAllApiCallBarrier);
    }

    console.log(`JWT refreshed:: Unblock request: ${clientRequest.apiCall.payload.fn.name}`);

    const response: ExtendedAxiosResponse = yield clientRequest.apiCall;

    return response;
  }
}

function* handleAndGetResponse(clientRequest: ApiClientRequest, response: ExtendedAxiosResponse): any {
  let responseRefreshed = response;
  const cancelAll = yield select(isAllApiCallBarrier);

  if (!cancelAll || (cancelAll && response?.status !== 530)) {
    const userInfo: UserInfo | null = yield select(selectUserInfo);

    if (response.status === 401) {
      if (userInfo?.logged && response?.headers['x-jwt-expired']) {
        responseRefreshed = yield call(doRefreshJwt, clientRequest);
      } else if (response?.headers['x-o2-jwt-state'] === 'NOT_MFA_AUTHENTICATED') {
        window.location.href = `${environment.buildProperties.bffAuthUrl}/casLogin?${
          environment.properties['gui.loginPageMfa.active'] === 'true' ? 'forceMfa=true&' : ''
        }redirectTo=${encodeURIComponent(window.location.href)}`;
      } else {
        yield putResolve(loginActions.request());
      }
    } else if (response.status === 530 && !clientRequest.suppressError) {
      if (environment.buildProperties.guiGeneralPlannedOutageUrl) {
        window.location.href = environment.buildProperties.guiGeneralPlannedOutageUrl;
      }

      yield put(setAllApiCallBarrierAction(true));

      return {};
    } else if (
      !response.ok &&
      (response.status === 412 || response.status === 400 || response.status >= 500) &&
      !clientRequest.skipDefaultKahlBehavior
    ) {
      // Send response to Kahl component
      yield put(
        getKahlOutsideComponentAction({
          ...response.data,
          suppressError: clientRequest.suppressError,
        })
      );
    } else if (
      response.status === 403 &&
      !response?.config?.params?.infoMessageIds &&
      !clientRequest.skipDefaultKahlBehavior
    ) {
      yield put(getKahlActions.request({ infoMessageIds: 'ID0018' }));
    } else if (
      response.status === 404 &&
      !response?.config?.params?.infoMessageIds &&
      !clientRequest.skipDefaultKahlBehavior
    ) {
      yield put(getKahlActions.request({ infoMessageIds: 'ID0013' }));
    }

    if (!clientRequest.skipUserActivityLog) {
      yield put(userActivityTimeAction());
    }

    return responseRefreshed;
  }

  return {};
}

/**
 * <b>This is the preferred way to call API.</b>
 * Performs requested API call and returns ExtendedAxiosResponse.
 * It also handles refreshJWT. If the JWT token expires, it blocks all further requests until the JWT is refreshed.
 * @param clientRequest {@link ApiClientRequest}
 */
export function* performApiCall(clientRequest: ApiClientRequest): any {
  let refreshJwtBarrierEnabled: boolean = yield select(isAllApiCallBarrier);

  if (refreshJwtBarrierEnabled) {
    while (refreshJwtBarrierEnabled) {
      yield delay(10); // protection delay; without it, the loop will automatically break after a while
      refreshJwtBarrierEnabled = yield select(isAllApiCallBarrier);
    }

    console.log(`JWT expired:: Unblock request: ${clientRequest.apiCall.payload.fn.name}`);

    const resp: ExtendedAxiosResponse = yield clientRequest.apiCall;

    return yield call(handleAndGetResponse, clientRequest, resp);
  } else {
    // no issue -> perform request
    const resp: ExtendedAxiosResponse = yield clientRequest.apiCall;

    return yield call(handleAndGetResponse, clientRequest, resp);
  }
}

export function getApiUrl(module: UrlSetup): any {
  const isProd = selectProperty('gui.isProdEnvironment');

  if (isProd?.toLowerCase() === 'true') {
    return selectProperty(module.propertyKey);
  }

  if (module?.mockEnabled) {
    return environment.mockUrl;
  } else {
    const url = selectProperty(module.propertyKey);

    return url || environment.mockUrl;
  }
}
